import { useState } from 'react';
import { useLoaderData, useNavigate, useRevalidator, useSearchParams } from 'react-router';
import type { LoaderFunctionArgs } from 'react-router';

import { Badge, Button, Icon, KpiCard, Progress } from '@ds/core';
import { DataTable, FilterBar } from '@ds/data';
import type { DataTableColumn } from '@ds/data';
import { SearchField } from '@ds/forms';
import { Menu, Pagination, SegmentedControl } from '@ds/navigation';
import { ExportMenu } from '@/comuns/ExportMenu';
import { PageHeader } from '@/comuns/PageHeader';
import { dataSimples, moeda } from '@/comuns/formato';
import { useAviso } from '@/comuns/toast';
import { dados } from '@/data/porta';
import { TODOS_OS_REGISTROS } from '@/comuns/exportar';
import type { AbaDeVendas, CargaDeVendas, CondicaoDeCobranca, Orcamento, Pacote, StatusDoOrcamento } from '@/data/tipos';
import { useRotaAtual } from '@/router/useRotaAtual';
import { atual } from '@/session/sessao';
import { AprovarOrcamentoModal } from './vendas/AprovarOrcamentoModal';
import { DetalheDoOrcamentoModal, STATUS_DO_ORCAMENTO, orcamentoEditavel } from './vendas/DetalheDoOrcamentoModal';
import { NovoOrcamentoModal } from './vendas/NovoOrcamentoModal';
import { imprimirOrcamento } from './vendas/imprimirOrcamento';

const POR_PAGINA = 8;

function abaDaRota(pathname: string): AbaDeVendas {
    if (pathname.endsWith('/aguardando')) return 'aguardando';
    if (pathname.endsWith('/aprovados')) return 'aprovados';
    if (pathname.endsWith('/pacotes')) return 'pacotes';
    return 'orcamentos';
}

export interface CargaDaTelaDeVendas extends CargaDeVendas {
    /**
     * O orçamento de `?orcamento=`. O detalhe mora na URL: recarregar a página,
     * mandar o link ou voltar reabre a mesma proposta.
     */
    orcamentoAberto: Orcamento | null;
    erroDoOrcamento: string | null;
}

export async function carregarVendas({ request }: LoaderFunctionArgs): Promise<CargaDaTelaDeVendas> {
    const url = new URL(request.url);
    const idAberto = url.searchParams.get('orcamento');
    const [carga, aberto] = await Promise.all([
        dados().vendas({
            aba: abaDaRota(url.pathname),
            busca: url.searchParams.get('q') ?? undefined,
            pagina: Number(url.searchParams.get('pagina') ?? '1') || 1,
            porPagina: POR_PAGINA,
        }),
        idAberto
            ? dados().orcamento(idAberto).then(
                (orcamento) => ({ orcamento, erro: null }),
                (e: unknown) => ({ orcamento: null, erro: e instanceof Error ? e.message : 'Orçamento não encontrado.' }),
            )
            : Promise.resolve(null),
    ]);
    return { ...carga, orcamentoAberto: aberto?.orcamento ?? null, erroDoOrcamento: aberto?.erro ?? null };
}

export function Vendas() {
    const carga = useLoaderData() as CargaDaTelaDeVendas;
    const [params, setParams] = useSearchParams();
    const navigate = useNavigate();
    const revalidator = useRevalidator();
    const aviso = useAviso();
    const handle = useRotaAtual();
    const [novoAberto, setNovoAberto] = useState(false);
    const [editando, setEditando] = useState<Orcamento | null>(null);
    const [duplicando, setDuplicando] = useState<Orcamento | null>(null);
    /* Aprovar pergunta antes como a venda será paga: as parcelas nascem junto. */
    const [aprovando, setAprovando] = useState<Orcamento | null>(null);

    const aba = (handle?.subrota?.path ?? 'orcamentos') as AbaDeVendas;
    const busca = params.get('q') ?? '';

    const trocarParam = (chave: string, valor: string | null) => {
        setParams((atuais) => {
            const proximos = new URLSearchParams(atuais);
            if (!valor) proximos.delete(chave);
            else proximos.set(chave, valor);
            /* Abrir o detalhe não muda o recorte: a página da lista fica. */
            if (chave !== 'pagina' && chave !== 'orcamento') proximos.delete('pagina');
            return proximos;
        }, { replace: chave !== 'orcamento' });
    };

    const decidir = async (orcamento: Orcamento, status: StatusDoOrcamento, cobranca?: CondicaoDeCobranca): Promise<boolean> => {
        if (status === 'aprovado' && !cobranca) {
            setAprovando(orcamento);
            return false;
        }
        try {
            await dados().decidirOrcamento(orcamento.id, status, cobranca);
            aviso({
                title: status === 'aprovado' ? 'Orçamento aprovado' : status === 'enviado' ? 'Orçamento enviado' : 'Orçamento recusado',
                description: `${orcamento.numero} · ${orcamento.paciente}`,
                tone: status === 'recusado' ? 'warning' : 'success',
            });
            revalidator.revalidate();
            return true;
        } catch (e) {
            aviso({ title: 'Não foi possível mudar a situação', description: e instanceof Error ? e.message : undefined, tone: 'danger' });
            return false;
        }
    };

    const gerarPdf = (o: Orcamento) => {
        if (!imprimirOrcamento(o, atual()?.clinica ?? 'Clínica')) {
            aviso({ title: 'Permita pop-ups para gerar o PDF', description: 'O navegador bloqueou a janela de impressão.', tone: 'warning' });
        }
    };

    /* O menu da linha traz os itens só com o resumo; editar, duplicar e o PDF
       precisam dos itens gravados. */
    const comItens = async (o: Orcamento, acao: (completo: Orcamento) => void) => {
        try {
            acao(await dados().orcamento(o.id));
        } catch (e) {
            aviso({ title: 'Não foi possível abrir o orçamento', description: e instanceof Error ? e.message : undefined, tone: 'danger' });
        }
    };

    const colunasDeOrcamento: DataTableColumn<Orcamento>[] = [
        { key: 'numero', header: 'Número', sortable: true },
        { key: 'paciente', header: 'Paciente', sortable: true },
        { key: 'resumo', header: 'Itens' },
        { key: 'responsavel', header: 'Responsável' },
        { key: 'criadoEm', header: 'Criado', render: (o) => dataSimples(o.criadoEm) },
        { key: 'validade', header: 'Validade', render: (o) => dataSimples(o.validade) },
        { key: 'status', header: 'Situação', render: (o) => <Badge tone={STATUS_DO_ORCAMENTO[o.status].tom} dot>{STATUS_DO_ORCAMENTO[o.status].rotulo}</Badge> },
        { key: 'totalCentavos', header: 'Valor', align: 'right', render: (o) => moeda(o.totalCentavos) },
        {
            key: 'acoes', header: '', align: 'right', width: 56,
            render: (o) => (
                <span data-parar-linha>
                    <Menu
                        align="right" width={220}
                        onSelect={(id) => {
                            if (id === 'abrir') trocarParam('orcamento', o.id);
                            else if (id === 'editar') void comItens(o, setEditando);
                            else if (id === 'duplicar') void comItens(o, setDuplicando);
                            else if (id === 'pdf') void comItens(o, gerarPdf);
                            else if (id === 'enviar') void decidir(o, 'enviado');
                            else if (id === 'aprovar') void decidir(o, 'aprovado');
                            else if (id === 'recusar') void decidir(o, 'recusado');
                            else if (id === 'paciente' && o.contatoId) navigate(`/app/contatos/${o.contatoId}`);
                        }}
                        trigger={<Button size="sm" variant="ghost" iconLeft={<Icon name="ellipsis" size={15} />} aria-label={`Ações de ${o.numero}`} />}
                        items={[
                            { id: 'abrir', label: 'Ver detalhes', icon: 'eye' },
                            { id: 'editar', label: 'Editar proposta', icon: 'pencil', disabled: !orcamentoEditavel(o) },
                            { id: 'duplicar', label: 'Duplicar', icon: 'copy' },
                            { id: 'pdf', label: 'Gerar PDF', icon: 'file-down' },
                            { id: 'divisor-1', label: '', divider: true },
                            { id: 'enviar', label: 'Marcar como enviado', icon: 'send', disabled: o.status !== 'rascunho' },
                            { id: 'aprovar', label: 'Aprovar', icon: 'circle-check', disabled: o.status === 'aprovado' },
                            { id: 'recusar', label: 'Recusar', icon: 'circle-x', tone: 'danger', disabled: o.status === 'recusado' },
                            { id: 'divisor-2', label: '', divider: true },
                            { id: 'paciente', label: 'Abrir paciente', icon: 'user' },
                        ]}
                    />
                </span>
            ),
        },
    ];

    const colunasDePacote: DataTableColumn<Pacote>[] = [
        { key: 'paciente', header: 'Paciente', sortable: true },
        { key: 'nome', header: 'Pacote' },
        {
            key: 'sessoesUsadas', header: 'Sessões',
            render: (p) => (
                <span style={{ display: 'block', minWidth: 150 }}>
                    <Progress label={`${p.sessoesUsadas} de ${p.sessoesTotais}`} value={Math.round((p.sessoesUsadas / p.sessoesTotais) * 100)} />
                </span>
            ),
        },
        { key: 'validade', header: 'Validade', render: (p) => dataSimples(p.validade) },
        { key: 'status', header: 'Situação', render: (p) => <Badge tone={p.status === 'ativo' ? 'success' : 'neutral'} dot>{p.status === 'ativo' ? 'Ativo' : 'Encerrado'}</Badge> },
        { key: 'valorCentavos', header: 'Valor', align: 'right', render: (p) => moeda(p.valorCentavos) },
    ];

    const detalheAberto = Boolean(params.get('orcamento'));

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <PageHeader
                title="Vendas"
                description="Orçamentos, aprovações e pacotes em uso"
                actions={
                    <>
                        <ExportMenu<Orcamento>
                            label="Exportar" size="md"
                            carregarTudo={async () => (await dados().vendas({
                                aba, busca: busca || undefined, pagina: 1, porPagina: TODOS_OS_REGISTROS,
                            })).orcamentos}
                            payload={{
                                title: 'Orçamentos', subtitle: `${carga.total} registros`,
                                rows: carga.orcamentos,
                                columns: [
                                    { header: 'Número', value: (o) => o.numero },
                                    { header: 'Paciente', value: (o) => o.paciente },
                                    { header: 'Itens', value: (o) => o.resumo },
                                    { header: 'Situação', value: (o) => STATUS_DO_ORCAMENTO[o.status].rotulo },
                                    { header: 'Valor', value: (o) => moeda(o.totalCentavos) },
                                ],
                            }}
                        />
                        <Button iconLeft={<Icon name="plus" size={15} />} onClick={() => setNovoAberto(true)}>Novo orçamento</Button>
                    </>
                }
            >
                <SegmentedControl
                    value={aba} ariaLabel="Recorte de vendas"
                    onChange={(v) => navigate({ pathname: `/app/vendas/${v}`, search: params.toString() ? `?${params}` : '' })}
                    options={[
                        { value: 'orcamentos', label: `Orçamentos (${carga.totais.orcamentos})` },
                        { value: 'aguardando', label: `Aguardando (${carga.totais.aguardando})` },
                        { value: 'aprovados', label: `Aprovados (${carga.totais.aprovados})` },
                        { value: 'pacotes', label: `Pacotes (${carga.totais.pacotes})` },
                    ]}
                />
            </PageHeader>

            <div className="vec-stagger" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: 12 }}>
                {carga.indicadores.map((i) => (
                    <KpiCard key={i.id} label={i.rotulo} value={i.valor} hint={i.apoio} icon={<Icon name={i.icone} size={16} />} />
                ))}
            </div>

            <FilterBar
                search={<SearchField value={busca} onChange={(v) => trocarParam('q', v || null)} placeholder="Buscar por paciente, número ou item" width={280} />}
                resultLabel={`${carga.total} ${aba === 'pacotes' ? 'pacotes' : 'orçamentos'}`}
            />

            {aba === 'pacotes' ? (
                <DataTable
                    columns={colunasDePacote} rows={carga.pacotes} rowKey="id"
                    onRowClick={(p) => p.contatoId && navigate(`/app/contatos/${p.contatoId}`)}
                    rowLabel={(p) => `${p.paciente} · ${p.nome}`}
                    emptyMessage="Nenhum pacote vendido ainda."
                />
            ) : (
                <DataTable
                    columns={colunasDeOrcamento} rows={carga.orcamentos} rowKey="id"
                    onRowClick={(o) => trocarParam('orcamento', o.id)}
                    rowLabel={(o) => `${o.numero} · ${o.paciente}`}
                    emptyMessage="Nenhum orçamento neste recorte."
                />
            )}

            <Pagination page={carga.pagina} pageCount={carga.paginas} total={carga.total} pageSize={POR_PAGINA} onChange={(p) => trocarParam('pagina', String(p))} />

            <NovoOrcamentoModal open={novoAberto} onClose={() => setNovoAberto(false)} onCriado={() => revalidator.revalidate()} />

            {aprovando && (
                <AprovarOrcamentoModal
                    key={aprovando.id} orcamento={aprovando}
                    onFechar={() => setAprovando(null)}
                    onAprovar={(cobranca) => decidir(aprovando, 'aprovado', cobranca)}
                />
            )}
            {detalheAberto && !editando && !duplicando && !aprovando && (
                <DetalheDoOrcamentoModal
                    orcamento={carga.orcamentoAberto} erro={carga.erroDoOrcamento}
                    onFechar={() => trocarParam('orcamento', null)}
                    onEditar={setEditando}
                    onDuplicar={setDuplicando}
                    onGerarPdf={gerarPdf}
                    onDecidir={(o, status) => { void decidir(o, status); }}
                />
            )}
            {editando && (
                <NovoOrcamentoModal
                    key={`editar-${editando.id}`} open orcamento={editando}
                    onClose={() => setEditando(null)} onCriado={() => revalidator.revalidate()}
                />
            )}
            {duplicando && (
                <NovoOrcamentoModal
                    key={`duplicar-${duplicando.id}`} open base={duplicando}
                    onClose={() => setDuplicando(null)}
                    onCriado={() => { trocarParam('orcamento', null); revalidator.revalidate(); }}
                />
            )}
        </div>
    );
}
